import {
  AllCounterpartyTypes,
  IndividualEntrepreneur as TIndividualEntrepreneur,
  LegalEntity as TLegalEntity,
} from 'shared/api/__generated__';

import type {
  TMedOrg,
  TMedOrgListItem,
  TMedorgWithCountersAndToken,
} from '../../api';

/**
 * Тип медицинской организации
 */
export enum MedOrgType {
  IndividualEntrepreneur = 'individualEntrepreneur',
  LegalEntity = 'legalEntity',
}

/**
 * Проверка на юридическое лицо
 */
export const isLegalEntity = (
  medOrg:
    | AllCounterpartyTypes
    | TMedOrgListItem
    | TMedorgWithCountersAndToken
    | TMedOrg
    | undefined,
): medOrg is TLegalEntity | (TMedOrgListItem & { info: TLegalEntity }) => {
  if (!medOrg) return false;
  const info = 'info' in medOrg ? medOrg.info : medOrg;
  return typeof (info as TLegalEntity).ogrn === 'string';
};

/**
 * Проверка на ИП
 */
export const isIndividualEntrepreneur = (
  medOrg:
    | AllCounterpartyTypes
    | TMedOrgListItem
    | TMedorgWithCountersAndToken
    | TMedOrg
    | undefined,
): medOrg is
  | TIndividualEntrepreneur
  | (TMedOrgListItem & { info: TIndividualEntrepreneur }) => {
  if (!medOrg) return false;
  const info = 'info' in medOrg ? medOrg.info : medOrg;

  return (
    (info as TIndividualEntrepreneur).ogrnip !== undefined &&
    typeof (info as TIndividualEntrepreneur).fullname === 'object'
  );
};

/**
 * Получить тип медицинской организации
 */
export const getMedOrgType = (
  medOrg: TMedOrgListItem | TMedorgWithCountersAndToken | undefined,
): MedOrgType => {
  if (isLegalEntity(medOrg)) {
    return MedOrgType.LegalEntity;
  }

  return MedOrgType.IndividualEntrepreneur;
};

/**
 * Получить название медицинской организации
 */
export const getMedOrgName = (
  medOrg?:
    | AllCounterpartyTypes
    | TMedOrgListItem
    | TMedorgWithCountersAndToken
    | TMedOrg,
): string => {
  if (isLegalEntity(medOrg)) {
    const info = 'info' in medOrg ? medOrg.info : medOrg;
    return info.name;
  }

  if (isIndividualEntrepreneur(medOrg)) {
    const info = 'info' in medOrg ? medOrg.info : medOrg;
    const { surname, name, patronymic } = info.fullname;
    return [surname, name, patronymic].filter(Boolean).join(' ');
  }

  return '';
};

/**
 * Получить краткое название медицинской организации
 */
export const getMedOrgShortname = (
  medOrg?:
    | AllCounterpartyTypes
    | TMedOrgListItem
    | TMedorgWithCountersAndToken
    | TMedOrg,
): string => {
  if (isLegalEntity(medOrg)) {
    const info = 'info' in medOrg ? medOrg.info : medOrg;
    return info.shortname ?? '';
  }

  return '';
};

/**
 * Получить значение поля isEnabled для медорганизации
 */
export const getMedOrgIsEnabled = (
  medOrg?: AllCounterpartyTypes | TMedOrgListItem | TMedorgWithCountersAndToken,
  defaultValue = false,
): boolean => {
  if (!medOrg) return defaultValue;

  const info = 'info' in medOrg ? medOrg.info : medOrg;

  return (info as { isEnabled?: boolean }).isEnabled ?? defaultValue;
};
